








































import { Component, Prop, Vue } from 'vue-property-decorator';
import { BForm, BFormGroup, BFormInput, BFormCheckbox, BContainer, BButton, BRow, BCol, BIconCloudUpload, BIconTrash, BFormTextarea, BFormInvalidFeedback, BOverlay, BFormCheckboxGroup } from 'bootstrap-vue';
import Configuration from '@/types/Configuration';
import Validator, { handleValidationErrors } from '@/types/Validator';
import AxiosService from '@/services/axiosService';
import i18n from '../i18n';

@Component({
    components: {
        'b-overlay': BOverlay,
        'b-container': BContainer,
        'b-row': BRow,
        'b-col':BCol,
        'b-form': BForm,
        'b-form-group': BFormGroup,
        'b-form-input': BFormInput,
        'b-form-invalid-feedback': BFormInvalidFeedback,
        'b-form-textarea': BFormTextarea,
        'b-form-checkbox-group': BFormCheckboxGroup,
        'b-form-checkbox': BFormCheckbox,
        'b-btn': BButton,
        'b-icon-cloud-upload': BIconCloudUpload,
        'b-icon-trash': BIconTrash
    }
})
export default class RoleDetails extends Vue {
    @Prop() roleId!: string

    apiAccessToken = this.$store.state.apiAccessToken
    isBusy = true;
    role: RoleData = {
        id: '',
        sequenceId: 0,
        name: '',
        isActive: true,
        operations: []
    }
    nameValidator: Validator = {
        isValid: null,
        errors: []
    }
    operations: string[] = [];
    selectedOperations: (string | null)[] = [];

    mounted() {
        this.apiAccessToken = this.$store.state.apiAccessToken;
        this.loadOperations();
        this.loadRole(this.roleId);
    }

    clearValidators() {
        this.nameValidator = {
            isValid: null,
            errors: []
        }
    }

    async loadRole(roleId: string) {
        if (roleId.length == 0) {
            this.isBusy = false;
            return;
        }

        this.isBusy = true;

        try {
            const response = await AxiosService.request<RoleData>({
                url: `rolesadministration/${roleId}`,
                method: 'GET'
            });

            this.role = response.data;
            this.selectedOperations = this.role.operations;
        }
        catch (error) {
            if (error.response) {
                switch (error.response.status) {
                    case 404:
                        return this.$router.push(`/${i18n.locale}/notfound`);
                    case 403:
                        return this.$router.push(`/${i18n.locale}/accessdenied`);
                    default:
                        return this.$router.push(`/${i18n.locale}/error/${error.response.status}`);
                }
            }
        }
        finally {
            this.isBusy = false;
        }
    }

    async loadOperations() {
        try {
            const response = await AxiosService.request<EntityList>({
                url: `operationslookup`,
                method: 'GET'
            });

            this.operations = response.data.records ?? [];
        }
        catch (error) {
            if (error.response) {
                switch (error.response.status) {
                    case 404:
                        return this.$router.push(`/${i18n.locale}/notfound`);
                    case 403:
                        return this.$router.push(`/${i18n.locale}/accessdenied`);
                    default:
                        return this.$router.push(`/${i18n.locale}/error/${error.response.status}`);
                }
            }
        }
    }

    async onSubmit(e: Event) { // eslint-disable-line @typescript-eslint/no-unused-vars
        this.isBusy = true;

        this.clearValidators();

        try {
            await AxiosService.request<boolean>({
                url: `rolesadministration`,
                method: 'POST',
                data: this.role
            });

            this.$root.$bvToast.toast(this.$t('toaster.roleSaved') as string, {
                title: this.$t('toaster.saveRoleTitle') as string,
                autoHideDelay: Configuration.ToasterAutoHideDelay,
                appendToast: true,
                variant: Configuration.ToasterVariantSuccess,
                toaster: Configuration.ToasterPosition
            });
            this.$emit('role-saved', true);
        }
        catch (error) {
            if (error.response) {
                switch (error.response.status) {
                    case 404:
                        return this.$router.push(`/${i18n.locale}/notfound`);
                    case 403:
                        return this.$router.push(`/${i18n.locale}/accessdenied`);
                    case 400:
                        handleValidationErrors(this, error.response.data.errors.Name, this.nameValidator);
                        return;
                    default:
                        return this.$router.push(`/${i18n.locale}/error/${error.response.status}`);
                }
            }
        }
        finally {
            this.isBusy = false;
        }
    }

    async deleteRole() {
        try {
            const response = await AxiosService.request({
                url: `rolesadministration/candelete/${this.roleId}`,
                method: 'GET'
            });

            const canDelete = response.data.canDelete;

            if (!canDelete) {
                this.$bvModal.msgBoxOk(
                    this.$t('modal.deniedRoleDeleteMessage', { roleName: this.role.name }) as string, {
                        title: this.$t('modal.deniedRoleDeleteTitle') as string,
                        centered: true,
                        buttonSize: 'sm',
                        okTitle: this.$t('modal.closeModal') as string
                    }
                );
                return;
            }

            this.$bvModal.msgBoxConfirm(this.$t('modal.confirmRoleDeleteMessage', { roleName: this.role.name }) as string, {
                    title: this.$t('modal.confirmRoleDeleteTitle') as string,
                    centered: true,
                    buttonSize: 'sm',
                    okTitle: this.$t('modal.confirmOkTitle') as string,
                    cancelTitle: this.$t('modal.confirmCancelTitle') as string
                })
                .then(value => {
                    if (value) {
                        this.isBusy = true;

                        try {
                            AxiosService.request<boolean>({
                                url: `rolesadministration?id=${this.roleId}`,
                                method: 'DELETE'
                            });

                            this.$root.$bvToast.toast(this.$t('toaster.roleDeleted', { roleName: this.role.name }) as string, {
                                title: this.$t('toaster.deleteRoleTitle') as string,
                                autoHideDelay: Configuration.ToasterAutoHideDelay,
                                appendToast: true,
                                variant: Configuration.ToasterVariantSuccess,
                                toaster: Configuration.ToasterPosition
                            });
                            this.$emit('role-deleted', true);
                        }
                        catch (error) {
                            if (error.response) {
                                switch (error.response.status) {
                                    case 404:
                                        return this.$router.push(`/${i18n.locale}/notfound`);
                                    case 403:
                                        return this.$router.push(`/${i18n.locale}/accessdenied`);
                                    default:
                                        return this.$router.push(`/${i18n.locale}/error/${error.response.status}`);
                                }
                            }
                        }
                        finally {
                            this.isBusy = false;
                        }
                    }
                })
                .catch(error => {
                    console.error(error);
                });
        }
        catch (error) {
            if (error.response) {
                switch (error.response.status) {
                    case 404:
                        return this.$router.push(`/${i18n.locale}/notfound`);
                    case 403:
                        return this.$router.push(`/${i18n.locale}/accessdenied`);
                    default:
                        return this.$router.push(`/${i18n.locale}/error/${error.response.status}`);
                }
            }
        }
        finally {
            this.isBusy = false;
        }
    }

    onOperationsChange(e: (string | null)[]) {
        this.selectedOperations = e;
        this.role.operations = [];
        this.selectedOperations.forEach((so) => {
            const ops = this.operations.filter((o) => { return o === so; });
            if (ops.length > 0) {
                this.role.operations.push(ops[0]);
            }
        });
    }
}

interface RoleData {
    id: string;
    sequenceId: number;
    name: string;
    operations: string[];
    isActive: boolean;
}

interface EntityList {
    records: string[];
}
